import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { X, Crop as CropIcon } from "lucide-react";

/**
 * Crops an image File against the given pixel area and returns a Blob.
 */
async function getCroppedBlob(imageSrc, pixelCrop, mimeType = "image/jpeg") {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );
  return new Promise((resolve) => canvas.toBlob(resolve, mimeType, 0.92));
}

/**
 * <ImageCropperModal />
 * Props:
 *  - file: File (required) — the user-picked file to crop
 *  - aspect: number (required) — e.g. 2 for 2:1, 1 for square, 16/9 for hero, null for free crop
 *  - presetLabel: string — small badge text shown in the header (e.g. "2:1 · Brand logo")
 *  - onCancel: () => void
 *  - onConfirm: (croppedFile: File) => void  // caller uploads the file as before
 */
export default function ImageCropperModal({ file, aspect, presetLabel, onCancel, onConfirm }) {
  const [imageSrc] = useState(() => URL.createObjectURL(file));
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleApply = async () => {
    if (!croppedAreaPixels) return;
    setBusy(true);
    try {
      const mime = file.type === "image/png" ? "image/png" : "image/jpeg";
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, mime);
      const ext = mime === "image/png" ? "png" : "jpg";
      const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
      const cropped = new File([blob], `${baseName}-cropped.${ext}`, { type: mime });
      onConfirm(cropped);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-slate-900/80 backdrop-blur-sm p-4"
      data-testid="image-cropper-modal"
    >
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <CropIcon className="w-4 h-4 text-amber-600" />
            <h3 className="font-heading text-base font-semibold text-slate-900">
              Crop your image
            </h3>
            {presetLabel && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-medium uppercase tracking-wider">
                {presetLabel}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-900"
            data-testid="cropper-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative bg-slate-900" style={{ height: 380 }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect || undefined}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
            restrictPosition
          />
        </div>

        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider w-12">
              Zoom
            </span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-amber-500"
              data-testid="cropper-zoom"
            />
            <span className="text-xs font-mono text-slate-500 w-10 text-right">
              {zoom.toFixed(1)}×
            </span>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"
              data-testid="cropper-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={busy || !croppedAreaPixels}
              className="px-5 py-2 text-sm rounded-full bg-slate-900 text-white hover:bg-amber-500 hover:text-slate-900 disabled:opacity-50"
              data-testid="cropper-apply"
            >
              {busy ? "Applying…" : "Apply & upload"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
